import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { Heart, X, MapPin, Loader2, RefreshCw, SlidersHorizontal, MoreVertical, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import MatchPopup from '../components/MatchPopup';
import DiscoverFilters from '../components/DiscoverFilters';
import ReportDialog from '../components/ReportDialog';
import NotificationBell from '../components/NotificationBell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Discover() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [users, setUsers] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [matchPopup, setMatchPopup] = useState(null);
  const [swiping, setSwiping] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [filters, setFilters] = useState({ minAge: 18, maxAge: 60, gender: null, maxDistance: 25 });

  useEffect(() => {
    if (!user?.is_paid) {
      navigate('/paywall');
      return;
    }
    if (!user?.profile_complete) {
      navigate('/app/profile/setup');
      return;
    }
    fetchUsers();
  }, [user, navigate]);

  const fetchUsers = async (appliedFilters) => {
    setLoading(true);
    setError('');
    const f = appliedFilters || filters;
    try {
      const params = new URLSearchParams();
      if (f.minAge) params.set('min_age', f.minAge);
      if (f.maxAge) params.set('max_age', f.maxAge);
      if (f.gender) params.set('gender', f.gender);
      if (f.maxDistance) params.set('max_distance', f.maxDistance);

      const { data } = await axios.get(`${API}/discover?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(data.users);
      setCurrentIndex(0);
    } catch (e) {
      if (e.response?.status === 403) {
        navigate('/paywall');
      } else if (e.response?.status === 400) {
        setError('Please enable location to discover matches');
      } else {
        setError(e.response?.data?.detail || 'Failed to load matches');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSwipe = async (action) => {
    if (swiping || currentIndex >= users.length) return;
    setSwiping(true);
    const targetUser = users[currentIndex];
    try {
      const { data } = await axios.post(`${API}/swipe`, {
        target_user_id: targetUser._id,
        action
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.is_match) {
        setMatchPopup({ user: targetUser, matchId: data.match_id });
      }
      setCurrentIndex(prev => prev + 1);
    } catch (e) {
      console.error('Swipe error:', e);
    } finally {
      setSwiping(false);
    }
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    fetchUsers(newFilters);
  };

  const handleBlockFromCard = () => {
    setShowReport(false);
    setCurrentIndex(prev => prev + 1);
  };

  const currentUser = users[currentIndex];

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="px-4 py-3 flex items-center justify-between">
        <h1 className="font-outfit text-xl font-bold text-white">Discover</h1>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <button
            data-testid="open-filters-btn"
            onClick={() => setShowFilters(true)}
            className="w-10 h-10 rounded-full bg-zinc-900 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
          >
            <SlidersHorizontal className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Filters Modal */}
      {showFilters && (
        <DiscoverFilters
          filters={filters}
          onApply={handleApplyFilters}
          onClose={() => setShowFilters(false)}
        />
      )}

      {/* Report Dialog */}
      {showReport && currentUser && (
        <ReportDialog
          userId={currentUser._id}
          userName={currentUser.name}
          onClose={() => setShowReport(false)}
          onBlocked={handleBlockFromCard}
        />
      )}

      {/* Match Popup */}
      <AnimatePresence>
        {matchPopup && (
          <MatchPopup
            user={matchPopup.user}
            matchId={matchPopup.matchId}
            onClose={() => setMatchPopup(null)}
          />
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1 px-4 pb-24">
        {loading && (
          <div className="flex-1 flex items-center justify-center h-[70vh]">
            <Loader2 className="w-10 h-10 text-rose-500 animate-spin" />
          </div>
        )}

        {error && (
          <div className="flex-1 flex flex-col items-center justify-center h-[70vh] text-center px-6">
            <p className="text-zinc-400 mb-4">{error}</p>
            <Button data-testid="retry-discover-btn" onClick={() => fetchUsers()} variant="outline" className="border-zinc-700 text-zinc-300">
              <RefreshCw className="w-4 h-4 mr-2" /> Try Again
            </Button>
          </div>
        )}

        {!loading && !error && users.length === 0 && (
          <div className="flex-1 flex flex-col items-center justify-center h-[70vh] text-center px-6">
            <div className="w-16 h-16 rounded-full bg-zinc-900 flex items-center justify-center mb-4">
              <Heart className="w-8 h-8 text-zinc-600" />
            </div>
            <h2 className="font-outfit text-xl font-medium text-white mb-2">No More Profiles</h2>
            <p className="text-zinc-500 mb-4">Check back later for new matches in your area</p>
            <Button data-testid="refresh-btn" onClick={() => fetchUsers()} variant="outline" className="border-zinc-700 text-zinc-300">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        )}

        {!loading && !error && currentUser && (
          <div className="max-w-md mx-auto">
            <div className="relative h-[60vh] max-h-[550px]">
              <AnimatePresence>
                <SwipeCard
                  key={currentUser._id}
                  user={currentUser}
                  onSwipeLeft={() => handleSwipe('pass')}
                  onSwipeRight={() => handleSwipe('like')}
                  onReport={() => setShowReport(true)}
                />
              </AnimatePresence>
            </div>

            <div className="flex items-center justify-center gap-6 mt-5">
              <button
                data-testid="pass-btn"
                onClick={() => handleSwipe('pass')}
                disabled={swiping}
                className="w-16 h-16 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white hover:border-zinc-600 transition-all hover:scale-110 active:scale-95"
              >
                <X className="w-8 h-8" />
              </button>
              <button
                data-testid="like-btn"
                onClick={() => handleSwipe('like')}
                disabled={swiping}
                className="w-20 h-20 rounded-full bg-rose-600 flex items-center justify-center text-white shadow-[0_8px_32px_rgba(225,29,72,0.4)] hover:bg-rose-500 transition-all hover:scale-110 active:scale-95"
              >
                <Heart className="w-10 h-10" />
              </button>
            </div>
          </div>
        )}
      </main>

      <BottomNav active="discover" />
    </div>
  );
}

function SwipeCard({ user, onSwipeLeft, onSwipeRight, onReport }) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-15, 15]);
  const likeOpacity = useTransform(x, [0, 100], [0, 1]);
  const nopeOpacity = useTransform(x, [-100, 0], [1, 0]);

  const handleDragEnd = (_, info) => {
    if (info.offset.x > 100) onSwipeRight();
    else if (info.offset.x < -100) onSwipeLeft();
  };

  const mainPhoto = user.photos?.[0] || 'https://images.unsplash.com/photo-1694877164163-bd51561f794a?w=400';

  return (
    <motion.div
      data-testid="swipe-card"
      className="absolute inset-0 rounded-3xl overflow-hidden cursor-grab active:cursor-grabbing touch-none"
      style={{ x, rotate }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      onDragEnd={handleDragEnd}
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ x: x.get() > 0 ? 300 : -300, opacity: 0, transition: { duration: 0.2 } }}
    >
      <img src={mainPhoto} alt={user.name} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

      {/* Report button */}
      <button
        data-testid="report-card-btn"
        onClick={(e) => { e.stopPropagation(); onReport(); }}
        className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-zinc-300 hover:text-white z-10"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {/* Like/Nope indicators */}
      <motion.div className="absolute top-8 right-16 px-4 py-2 rounded-lg border-2 border-green-500 text-green-500 font-bold text-xl rotate-12" style={{ opacity: likeOpacity }}>LIKE</motion.div>
      <motion.div className="absolute top-8 left-8 px-4 py-2 rounded-lg border-2 border-red-500 text-red-500 font-bold text-xl -rotate-12" style={{ opacity: nopeOpacity }}>NOPE</motion.div>

      {/* User info */}
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <div className="flex items-end justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-outfit text-2xl font-semibold text-white">{user.name}, {user.age}</h2>
              {user.verified && <ShieldCheck className="w-5 h-5 text-blue-400" />}
            </div>
            <div className="flex items-center gap-1 text-zinc-400 mt-1">
              <MapPin className="w-4 h-4" />
              <span>{user.distance} miles away</span>
            </div>
            {user.bio && <p className="text-zinc-300 text-sm mt-2 line-clamp-2">{user.bio}</p>}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
